import {
  attributeGroup,
  lookupBucket,
  MapLayerId,
  MapSource,
  marker,
  pointLayer,
  remoteGeojsonSource,
  remoteTileSource,
  simpleAttr,
  standardAttribution,
  stringAttr,
  unsafeMapId,
  unsafeMapLayerId,
} from "@cartographerio/atlas-map";
import { mapSchemaWithDefaults } from "../../core";
import {
  NeChalkRivers,
  proposedChalkRiversModificationsLayer,
  publishedChalkRiversLayer,
} from "./NeChalkRivers";

const emptySource = remoteGeojsonSource({
  url: "http://priorityhabitats.org/wp-content/empty-map-layer.geojson",
  attribution: [],
});

const nePredHabMapEnglandSource: MapSource = remoteTileSource({
  url: "mapbox://cartographerio.ne_pred_hab_map_england",
  sourceLayer: "pred_hab_map_england",
  attribution: [
    standardAttribution({
      what: "Predicted Habitat Map",
      who: "Natural England",
      from: "November 2022",
      url: "https://priorityhabitats.org",
    }),
  ],
});

export const NeRiverTypes = mapSchemaWithDefaults({
  mapId: unsafeMapId("neRiverTypes"),
  title: "River Types",
  defaultBaseStyleId: "satellite",
  permissionModel: NeChalkRivers.permissionModel,
  collaborationModel: NeChalkRivers.collaborationModel,
  layers: [
    pointLayer({
      layerId: unsafeMapLayerId("h3260"),
      title: "Habitats Directive H3260",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "h3260_high",
              label: "High Certainty Only",
              propertyName: "h3260",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#f7f", 0.75, 0.25),
                  "High"
                ),
              ],
            }),
            stringAttr({
              attributeId: "h3260_all",
              label: "All Certainties",
              propertyName: "h3260",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#f7f", 0.75, 0.25),
                  "High"
                ),
                lookupBucket(
                  "MEDIUM",
                  marker.fromColor("#b5b", 0.75, 0.25),
                  "Medium"
                ),
                lookupBucket(
                  "LOW",
                  marker.fromColor("#848", 0.75, 0.25),
                  "Low"
                ),
              ],
            }),
          ],
        }),
      ],
    }),
    publishedChalkRiversLayer(
      unsafeMapLayerId("chalkRivers"),
      "Chalk Rivers",
      marker.fromColor("#ff0", 0.75, 0.25),
      marker.fromColor("#aa0", 0.75, 0.25)
    ),
    // placeholderLayer(unsafeMapLayerId("headwaterStreams"), "Headwater Streams"),
    pointLayer({
      layerId: unsafeMapLayerId("hw"),
      title: "Headwater Streams",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "hw_high",
              label: "High Certainty Only",
              propertyName: "hw",
              buckets: [
                lookupBucket(
                  "HW",
                  marker.fromColor("#0ff", 0.75, 0.25),
                  "High"
                ),
              ],
            }),
            stringAttr({
              attributeId: "hw_all",
              label: "All Certainties",
              propertyName: "hw",
              buckets: [
                lookupBucket(
                  "HW",
                  marker.fromColor("#0ff", 0.75, 0.25),
                  "High"
                ),
                lookupBucket("NA", marker.fromColor("#0aa", 0.75, 0.25), "Low"),
              ],
            }),
          ],
        }),
      ],
    }),
    // placeholderLayer(
    //   unsafeMapLayerId("activeShingleRivers"),
    //   "Active Shingle Rivers"
    // ),
    pointLayer({
      layerId: unsafeMapLayerId("pccrlsas"),
      title: "Active Shingle Rivers",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "pccrlsas_high",
              label: "High Certainty Only",
              propertyName: "pccrlsas",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#fff", 0.75, 0.25),
                  "High"
                ),
              ],
            }),
            stringAttr({
              attributeId: "pccrlsas_all",
              label: "All Certainties",
              propertyName: "pccrlsas",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#fff", 0.75, 0.25),
                  "High"
                ),
                lookupBucket(
                  "MEDIUM",
                  marker.fromColor("#ccc", 0.75, 0.25),
                  "Medium"
                ),
                lookupBucket(
                  "LOW",
                  marker.fromColor("#aaa", 0.75, 0.25),
                  "Low"
                ),
              ],
            }),
          ],
        }),
      ],
    }),
    // placeholderLayer(
    //   unsafeMapLayerId("europeanRedListTypes"),
    //   "European Red List Types"
    // ),
    pointLayer({
      layerId: unsafeMapLayerId("pccrlc22a"),
      title: "C2.2a High Energy",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "pccrlc22a_high",
              label: "High Certainty Only",
              propertyName: "pccrlc22a",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#f77", 0.75, 0.25),
                  "High"
                ),
              ],
            }),
            stringAttr({
              attributeId: "pccrlc22a_all",
              label: "All Certainties",
              propertyName: "pccrlc22a",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#f77", 0.75, 0.25),
                  "High"
                ),
                lookupBucket(
                  "MEDIUM",
                  marker.fromColor("#b55", 0.75, 0.25),
                  "Medium"
                ),
                lookupBucket(
                  "LOW",
                  marker.fromColor("#844", 0.75, 0.25),
                  "Low"
                ),
              ],
            }),
          ],
        }),
      ],
    }),
    pointLayer({
      layerId: unsafeMapLayerId("pccrlc22b"),
      title: "C2.2b Moderate Energy",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "pccrlc22b_high",
              label: "High Certainty Only",
              propertyName: "pccrlc22b",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#0f0", 0.75, 0.25),
                  "High"
                ),
              ],
            }),
            stringAttr({
              attributeId: "pccrlc22b_all",
              label: "All Certainties",
              propertyName: "pccrlc22b",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#0f0", 0.75, 0.25),
                  "High"
                ),
                lookupBucket(
                  "MEDIUM",
                  marker.fromColor("#0b0", 0.75, 0.25),
                  "Medium"
                ),
                lookupBucket(
                  "LOW",
                  marker.fromColor("#080", 0.75, 0.25),
                  "Low"
                ),
              ],
            }),
          ],
        }),
      ],
    }),
    pointLayer({
      layerId: unsafeMapLayerId("pccrlc23"),
      title: "C2.3 Low Energy",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "pccrlc23_high",
              label: "High Certainty Only",
              propertyName: "pccrlc23",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#77f", 0.75, 0.25),
                  "High"
                ),
              ],
            }),
            stringAttr({
              attributeId: "pccrlc23_all",
              label: "All Certainties",
              propertyName: "pccrlc23",
              buckets: [
                lookupBucket(
                  "HIGH",
                  marker.fromColor("#77f", 0.75, 0.25),
                  "High"
                ),
                lookupBucket(
                  "MEDIUM",
                  marker.fromColor("#55b", 0.75, 0.25),
                  "Medium"
                ),
                lookupBucket(
                  "LOW",
                  marker.fromColor("#448", 0.75, 0.25),
                  "Low"
                ),
              ],
            }),
          ],
        }),
      ],
    }),
    pointLayer({
      layerId: unsafeMapLayerId("tidal"),
      title: "Tidally Influenced Rivers",
      source: nePredHabMapEnglandSource,
      attributes: [
        attributeGroup({
          attributes: [
            stringAttr({
              attributeId: "tidal",
              label: "Tidally Influenced Rivers",
              propertyName: "tidal",
              buckets: [
                lookupBucket(
                  "TIDAL",
                  marker.fromColor("#33f", 0.75, 0.25),
                  "Tidally Influenced"
                ),
              ],
            }),
          ],
        }),
      ],
    }),
    proposedChalkRiversModificationsLayer,
  ],
});
